import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePaymentMode } from '../contexts/PaymentModeContext';
import { HostDirectModeBanner } from '../components/HostDirectModeBanner';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DollarSign, Clock, CheckCircle, XCircle, RefreshCw, User,
  AlertTriangle, Building2, ChevronDown, ChevronUp, FileText,
  ArrowRightLeft, Send, Ban, RotateCcw, CreditCard, Filter
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { PlatformOwnerAppShell } from '../components/PlatformOwnerAppShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtAUD = (cents) => {
  if (cents == null) return '\u2014';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2
  }).format(cents / 100);
};

const fmtDate = (iso) => {
  if (!iso) return '\u2014';
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
};

const STATUS_CONFIG = {
  not_eligible: { label: 'Not Eligible', icon: Ban, color: 'bg-slate-600', text: 'text-slate-300', border: 'border-slate-600/30', bg: 'bg-slate-800/40' },
  eligible:     { label: 'Eligible',     icon: DollarSign, color: 'bg-amber-600', text: 'text-amber-300', border: 'border-amber-500/30', bg: 'bg-amber-900/20' },
  scheduled:    { label: 'Scheduled',    icon: Clock, color: 'bg-blue-600', text: 'text-blue-300', border: 'border-blue-500/30', bg: 'bg-blue-900/20' },
  paid:         { label: 'Paid',         icon: CheckCircle, color: 'bg-emerald-600', text: 'text-emerald-300', border: 'border-emerald-500/30', bg: 'bg-emerald-900/20' },
  failed:       { label: 'Failed',       icon: XCircle, color: 'bg-red-600', text: 'text-red-300', border: 'border-red-500/30', bg: 'bg-red-900/20' },
};

export default function PlatformOwnerPayouts() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isHostDirect } = usePaymentMode();
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  // Action dialog state
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, item: null });
  const [actionForm, setActionForm] = useState({ payout_method_used: 'bank_transfer', payout_reference: '', failure_reason: '', admin_note: '' });
  const [actionLoading, setActionLoading] = useState(false);

  // Audit dialog
  const [auditDialog, setAuditDialog] = useState({ open: false, bookingId: null, trail: [] });

  // Expanded row
  const [expandedId, setExpandedId] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/platform-owner/manual-payouts/queue`, {
        params: { status_filter: filter }, headers
      });
      setItems(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error('Failed to load payout queue');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!authLoading && user?.is_platform_owner) fetchQueue();
  }, [authLoading, user, fetchQueue]);

  const doAction = async (type, bookingId, body = {}) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/platform-owner/manual-payouts/${bookingId}/${type}`, body, { headers });
      const labels = { schedule: 'Scheduled', 'mark-paid': 'Marked as Paid', 'mark-failed': 'Marked as Failed', revert: 'Reverted to Eligible' };
      toast.success(labels[type] || 'Action completed');
      setActionDialog({ open: false, type: null, item: null });
      setActionForm({ payout_method_used: 'bank_transfer', payout_reference: '', failure_reason: '', admin_note: '' });
      await fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const openAudit = async (bookingId) => {
    try {
      const res = await axios.get(`${API_URL}/api/platform-owner/manual-payouts/${bookingId}/audit`, { headers });
      setAuditDialog({ open: true, bookingId, trail: res.data.audit_trail || [] });
    } catch {
      toast.error('Failed to load audit trail');
    }
  };

  if (authLoading) return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
      <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
    </div>
  );

  if (!user?.is_platform_owner) return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
      <Card className="bg-red-900/30 border-red-500/30">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-300">Platform Owner access required</p>
          <Button onClick={() => navigate('/')} className="mt-4">Go Home</Button>
        </CardContent>
      </Card>
    </div>
  );

  const totalAll = Object.values(counts).reduce((s, c) => s + c, 0);

  const headerActions = (
    <Button variant="outline" size="sm" onClick={fetchQueue} disabled={loading}
      className="border-slate-600 text-slate-300" data-testid="refresh-payouts">
      <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
    </Button>
  );

  return (
    <PlatformOwnerAppShell title="Manual Payouts" activeId="payouts" headerActions={headerActions}>
      {isHostDirect && (
        <div
          className="mb-4 p-3 rounded-lg border border-amber-400/40 bg-amber-500/10 text-amber-200 text-sm"
          data-testid="payouts-hostdirect-banner"
        >
          <p className="font-semibold">HireHub does not process payouts in direct payment mode</p>
          <p className="text-amber-200/80 text-xs mt-0.5">
            Hosts are paid directly by renters (PayID, bank transfer, etc.). The payout queue below
            is retained for historical records and for future reactivation of platform payments.
          </p>
        </div>
      )}
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {['not_eligible', 'eligible', 'scheduled', 'paid', 'failed'].map(key => {
          const cfg = STATUS_CONFIG[key];
          const Icon = cfg.icon;
          const count = counts[key] || 0;
          return (
            <Card key={key}
              className={`${cfg.bg} ${cfg.border} border cursor-pointer transition-all ${filter === key ? 'ring-2 ring-white/30' : 'hover:ring-1 hover:ring-white/10'}`}
              onClick={() => setFilter(f => f === key ? 'all' : key)}
              data-testid={`summary-card-${key}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${cfg.text}`} />
                  <span className={`text-xs ${cfg.text}`}>{cfg.label}</span>
                </div>
                <p className={`text-2xl font-bold ${cfg.text}`}>{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filter indicator */}
      {filter !== 'all' && (
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-400">Showing: <span className="text-white font-medium">{STATUS_CONFIG[filter]?.label}</span></span>
          <Button variant="ghost" size="sm" onClick={() => setFilter('all')} className="text-slate-400 text-xs h-6 px-2">
            Clear
          </Button>
        </div>
      )}

      {/* Queue Items */}
      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <p className="text-slate-300">{filter === 'all' ? 'No paid bookings in queue' : `No ${STATUS_CONFIG[filter]?.label.toLowerCase()} payouts`}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <PayoutQueueItem
              key={item.booking_id}
              item={item}
              expanded={expandedId === item.booking_id}
              onToggle={() => setExpandedId(id => id === item.booking_id ? null : item.booking_id)}
              onAction={(type) => {
                setActionForm({ payout_method_used: 'bank_transfer', payout_reference: '', failure_reason: '', admin_note: '' });
                setActionDialog({ open: true, type, item });
              }}
              onAudit={() => openAudit(item.booking_id)}
            />
          ))}
        </div>
      )}

      {/* ─── ACTION DIALOG ─── */}
      <Dialog open={actionDialog.open} onOpenChange={(o) => !o && setActionDialog({ open: false, type: null, item: null })}>
        <DialogContent className="sm:max-w-md" data-testid="payout-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionDialog.type === 'schedule' && <><Send className="w-5 h-5 text-blue-400" /> Schedule Payout</>}
              {actionDialog.type === 'mark-paid' && <><CheckCircle className="w-5 h-5 text-emerald-400" /> Mark as Paid</>}
              {actionDialog.type === 'mark-failed' && <><XCircle className="w-5 h-5 text-red-400" /> Mark as Failed</>}
              {actionDialog.type === 'revert' && <><RotateCcw className="w-5 h-5 text-amber-400" /> Revert to Eligible</>}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.item?.booking_id} &mdash; {fmtAUD(actionDialog.item?.host_payout_cents)} to {actionDialog.item?.host_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Mark Paid: method + reference (required) */}
            {actionDialog.type === 'mark-paid' && (
              <>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Payout Method *</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { val: 'bank_transfer', label: 'Bank Transfer', icon: Building2 },
                      { val: 'payid', label: 'PayID', icon: CreditCard },
                      { val: 'manual_other', label: 'Other', icon: ArrowRightLeft },
                    ].map(m => (
                      <button key={m.val} type="button"
                        className={`flex items-center gap-1.5 p-2 rounded-lg border text-sm transition-colors ${
                          actionForm.payout_method_used === m.val
                            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                            : 'border-slate-600 text-slate-400 hover:border-slate-500'
                        }`}
                        onClick={() => setActionForm(f => ({ ...f, payout_method_used: m.val }))}
                        data-testid={`method-${m.val}`}>
                        <m.icon className="w-3.5 h-3.5" /> {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="payout-ref" className="text-sm font-medium">Payout Reference *</Label>
                  <Input id="payout-ref" placeholder="e.g. BSB-TRF-20260409-001"
                    value={actionForm.payout_reference}
                    onChange={e => setActionForm(f => ({ ...f, payout_reference: e.target.value }))}
                    data-testid="payout-reference-input" />
                </div>
              </>
            )}

            {/* Mark Failed: reason (required) */}
            {actionDialog.type === 'mark-failed' && (
              <div className="space-y-1.5">
                <Label htmlFor="fail-reason" className="text-sm font-medium">Failure Reason *</Label>
                <Input id="fail-reason" placeholder="e.g. Bank rejected transfer — invalid BSB"
                  value={actionForm.failure_reason}
                  onChange={e => setActionForm(f => ({ ...f, failure_reason: e.target.value }))}
                  data-testid="failure-reason-input" />
              </div>
            )}

            {/* All actions: optional note */}
            {actionDialog.type !== 'revert' && (
              <div className="space-y-1.5">
                <Label htmlFor="admin-note" className="text-sm font-medium text-slate-400">Admin Note (optional)</Label>
                <Input id="admin-note" placeholder="Internal note..."
                  value={actionForm.admin_note}
                  onChange={e => setActionForm(f => ({ ...f, admin_note: e.target.value }))}
                  data-testid="admin-note-input" />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, item: null })}
              disabled={actionLoading}>Cancel</Button>
            <Button
              disabled={actionLoading ||
                (actionDialog.type === 'mark-paid' && !actionForm.payout_reference.trim()) ||
                (actionDialog.type === 'mark-failed' && !actionForm.failure_reason.trim())}
              onClick={() => {
                const id = actionDialog.item?.booking_id;
                if (actionDialog.type === 'schedule') doAction('schedule', id, { admin_note: actionForm.admin_note || null });
                else if (actionDialog.type === 'mark-paid') doAction('mark-paid', id, { payout_method_used: actionForm.payout_method_used, payout_reference: actionForm.payout_reference, admin_note: actionForm.admin_note || null });
                else if (actionDialog.type === 'mark-failed') doAction('mark-failed', id, { failure_reason: actionForm.failure_reason, admin_note: actionForm.admin_note || null });
                else if (actionDialog.type === 'revert') doAction('revert', id);
              }}
              className={
                actionDialog.type === 'mark-paid' ? 'bg-emerald-600 hover:bg-emerald-700' :
                actionDialog.type === 'mark-failed' ? 'bg-red-600 hover:bg-red-700' :
                actionDialog.type === 'revert' ? 'bg-amber-600 hover:bg-amber-700' :
                'bg-blue-600 hover:bg-blue-700'
              }
              data-testid="payout-action-confirm-btn">
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              {actionDialog.type === 'schedule' ? 'Schedule' :
               actionDialog.type === 'mark-paid' ? 'Confirm Paid' :
               actionDialog.type === 'mark-failed' ? 'Confirm Failed' : 'Revert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── AUDIT TRAIL DIALOG ─── */}
      <Dialog open={auditDialog.open} onOpenChange={(o) => !o && setAuditDialog({ open: false, bookingId: null, trail: [] })}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="audit-trail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-400" /> Payout Audit Trail
            </DialogTitle>
            <DialogDescription>{auditDialog.bookingId}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {auditDialog.trail.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No audit records</p>
            ) : auditDialog.trail.map((a, i) => {
              const prevCfg = STATUS_CONFIG[a.previous_status] || STATUS_CONFIG.not_eligible;
              const newCfg = STATUS_CONFIG[a.new_status] || STATUS_CONFIG.not_eligible;
              return (
                <div key={i} className="border border-slate-700/50 rounded-lg p-3 bg-slate-800/30" data-testid={`audit-entry-${i}`}>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <Badge className={`${prevCfg.color} text-xs`}>{a.previous_status}</Badge>
                    <ArrowRightLeft className="w-3 h-3 text-slate-500" />
                    <Badge className={`${newCfg.color} text-xs`}>{a.new_status}</Badge>
                    <span className="text-xs text-slate-500 ml-auto">{fmtDate(a.acted_at)}</span>
                  </div>
                  <p className="text-xs text-slate-400">by {a.acted_by_email}</p>
                  {a.payout_method_used && <p className="text-xs text-slate-400 mt-1">Method: {a.payout_method_used}</p>}
                  {a.payout_reference && <p className="text-xs text-emerald-400 mt-0.5 font-mono">Ref: {a.payout_reference}</p>}
                  {a.failure_reason && <p className="text-xs text-red-400 mt-0.5">Reason: {a.failure_reason}</p>}
                  {a.admin_note && <p className="text-xs text-slate-300 mt-0.5 italic">Note: {a.admin_note}</p>}
                  {a.net_payout_cents > 0 && <p className="text-xs text-slate-500 mt-0.5">Amount: {fmtAUD(a.net_payout_cents)}</p>}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </PlatformOwnerAppShell>
  );
}


function PayoutQueueItem({ item, expanded, onToggle, onAction, onAudit }) {
  const cfg = STATUS_CONFIG[item.payout_status] || STATUS_CONFIG.not_eligible;
  const Icon = cfg.icon;

  return (
    <Card className={`${cfg.bg} ${cfg.border} border transition-all`} data-testid={`payout-row-${item.booking_id}`}>
      <CardContent className="p-0">
        {/* Main Row */}
        <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer" onClick={onToggle}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm text-white truncate">{item.booking_id}</span>
              <Badge className={`${cfg.color} text-xs`} data-testid={`status-badge-${item.booking_id}`}>
                <Icon className="w-3 h-3 mr-1" /> {cfg.label}
              </Badge>
            </div>
            <div className="flex items-center gap-2 mt-1 text-sm text-slate-400">
              <User className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{item.host_name}</span>
              {item.host_email && <span className="text-slate-500 text-xs truncate hidden sm:inline">({item.host_email})</span>}
            </div>
            {item.vehicle_title && <p className="text-xs text-slate-500 mt-0.5 truncate">{item.vehicle_title}</p>}
          </div>

          {/* Amounts */}
          <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
            <div className="text-center">
              <p className="text-xs text-slate-500">Host Payout</p>
              <p className={`text-lg font-bold ${cfg.text}`} data-testid={`payout-amount-${item.booking_id}`}>
                {fmtAUD(item.host_payout_cents)}
              </p>
            </div>
            <div className="text-center hidden sm:block">
              <p className="text-xs text-slate-500">Platform Fee</p>
              <p className="text-sm text-slate-300">{fmtAUD(item.platform_fee_cents)}</p>
            </div>
          </div>

          {/* Expand toggle */}
          <div className="flex-shrink-0">
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-slate-700/30 pt-3 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-500">Booking Total</p>
                <p className="text-slate-300">{fmtAUD(item.gross_rental_cents)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Platform Fee</p>
                <p className="text-slate-300">{fmtAUD(item.platform_fee_cents)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Net Host Payout</p>
                <p className="text-emerald-300 font-semibold">{fmtAUD(item.host_payout_cents)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Booking Status</p>
                <p className="text-slate-300 capitalize">{item.booking_status?.replace(/_/g, ' ')}</p>
              </div>
            </div>

            {/* Payout Destination */}
            {item.payout_destination && (
              <div className="bg-slate-700/30 rounded-lg p-2.5">
                <p className="text-xs text-slate-500 mb-1">Payout Destination</p>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-200" data-testid={`payout-dest-${item.booking_id}`}>
                    {item.payout_destination.summary}
                  </span>
                </div>
              </div>
            )}

            {/* Block Reasons */}
            {item.block_reasons?.length > 0 && (
              <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-2.5">
                <p className="text-xs text-red-400 font-medium mb-1">Block Reasons</p>
                {item.block_reasons.map((r, i) => (
                  <p key={i} className="text-sm text-red-300 flex items-center gap-1.5" data-testid={`block-reason-${item.booking_id}-${i}`}>
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {r}
                  </p>
                ))}
              </div>
            )}

            {/* Paid info */}
            {item.payout_status === 'paid' && (
              <div className="bg-emerald-900/20 border border-emerald-500/20 rounded-lg p-2.5">
                <p className="text-xs text-emerald-400 font-medium mb-1">Payment Record</p>
                <p className="text-sm text-emerald-300">Method: {item.payout_method_used || '\u2014'}</p>
                <p className="text-sm text-emerald-300 font-mono">Ref: {item.payout_reference || '\u2014'}</p>
                <p className="text-xs text-emerald-400/70 mt-1">Paid: {fmtDate(item.payout_paid_at)}</p>
              </div>
            )}

            {/* Failed info */}
            {item.payout_status === 'failed' && item.payout_failure_reason && (
              <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-2.5">
                <p className="text-xs text-red-400 font-medium mb-1">Failure Info</p>
                <p className="text-sm text-red-300">{item.payout_failure_reason}</p>
                <p className="text-xs text-red-400/70 mt-1">Failed: {fmtDate(item.payout_failed_at)}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 pt-1">
              {item.payout_status === 'eligible' && (
                <>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700"
                    onClick={(e) => { e.stopPropagation(); onAction('schedule'); }}
                    data-testid={`action-schedule-${item.booking_id}`}>
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Schedule
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={(e) => { e.stopPropagation(); onAction('mark-paid'); }}
                    data-testid={`action-mark-paid-${item.booking_id}`}>
                    <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Mark Paid
                  </Button>
                </>
              )}
              {item.payout_status === 'scheduled' && (
                <>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={(e) => { e.stopPropagation(); onAction('mark-paid'); }}
                    data-testid={`action-mark-paid-${item.booking_id}`}>
                    <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Mark Paid
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-900/30"
                    onClick={(e) => { e.stopPropagation(); onAction('mark-failed'); }}
                    data-testid={`action-mark-failed-${item.booking_id}`}>
                    <XCircle className="w-3.5 h-3.5 mr-1.5" /> Mark Failed
                  </Button>
                  <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-amber-900/30"
                    onClick={(e) => { e.stopPropagation(); onAction('revert'); }}
                    data-testid={`action-revert-${item.booking_id}`}>
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Revert
                  </Button>
                </>
              )}
              {item.payout_status === 'failed' && (
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700"
                  onClick={(e) => { e.stopPropagation(); onAction('schedule'); }}
                  data-testid={`action-retry-${item.booking_id}`}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry (Schedule)
                </Button>
              )}
              <Button size="sm" variant="ghost" className="text-slate-400 hover:text-slate-200"
                onClick={(e) => { e.stopPropagation(); onAudit(); }}
                data-testid={`action-audit-${item.booking_id}`}>
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Audit Trail
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
